import { useTranslations } from "next-intl";
import { LocalVideoTrack } from "livekit-client";
import { Mic, MicOff, Video, VideoOff } from "lucide-react";

interface VideoPreviewProps {
  videoTrack: LocalVideoTrack | undefined;
  videoEnabled: boolean;
  audioEnabled: boolean;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  onToggleVideo: () => void;
  onToggleAudio: () => void;
}

/**
 * Video preview panel with camera feed and toggle controls.
 */
export const VideoPreview = ({
  videoTrack,
  videoEnabled,
  audioEnabled,
  videoRef,
  onToggleVideo,
  onToggleAudio
}: VideoPreviewProps) => {
  const t = useTranslations("preJoin");

  return (
    <div className="relative aspect-4/3 sm:aspect-video bg-gray-900 rounded-2xl sm:rounded-4xl overflow-hidden shadow-2xl ring-1 ring-black/10 group max-h-[35vh] sm:max-h-none">
      {videoTrack && videoEnabled ? (
        <video ref={videoRef} className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-linear-to-br from-gray-900 via-gray-800 to-gray-900">
          <div className="w-16 h-16 sm:w-32 sm:h-32 rounded-full bg-gray-800/80 border border-gray-700 flex items-center justify-center mb-2 sm:mb-6 shadow-2xl relative">
            <div className="absolute inset-0 rounded-full bg-primary/5 animate-pulse" />
            <VideoOff className="w-6 h-6 sm:w-12 sm:h-12 text-gray-500" />
          </div>
          <p className="text-sm sm:text-lg font-medium text-gray-400 tracking-wide">{t("cameraOff")}</p>
        </div>
      )}

      {/* Overlay Controls */}
      <div className="absolute bottom-3 sm:bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-3 sm:gap-6 md:gap-8 z-10">
        <button
          type="button"
          onClick={onToggleAudio}
          className={`p-3 sm:p-5 md:p-6 rounded-full transition-all duration-200 shadow-lg ${
            audioEnabled ? "bg-primary hover:bg-primary-hover text-white" : "bg-error hover:bg-error-light text-white"
          }`}
          title={audioEnabled ? "Mute microphone (Ctrl + D)" : "Unmute microphone (Ctrl + D)"}
        >
          {audioEnabled ? (
            <Mic className="w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8" />
          ) : (
            <MicOff className="w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8" />
          )}
        </button>

        <button
          type="button"
          onClick={onToggleVideo}
          className={`p-3 sm:p-5 md:p-6 rounded-full transition-all duration-200 shadow-lg ${
            videoEnabled ? "bg-primary hover:bg-primary-hover text-white" : "bg-error hover:bg-error-light text-white"
          }`}
          title={videoEnabled ? "Turn off camera (Ctrl + E)" : "Turn on camera (Ctrl + E)"}
        >
          {videoEnabled ? (
            <Video className="w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8" />
          ) : (
            <VideoOff className="w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8" />
          )}
        </button>
      </div>
    </div>
  );
};
